import React, { useEffect, useState } from 'react';
import { Box, Divider, HStack, VStack } from 'native-base';
import {
    faBirthdayCake,
    faEnvelope,
    faHome,
    faPhone,
    faUserCircle,
} from '@fortawesome/free-solid-svg-icons';
import { Member, useAppSettings, useTheme, useUpdateMember } from '@springbig/shared';
import { SpIcon } from '~/shared/Icon';
import { Caption, H1, H4 } from '~/shared/Typography';
import { useAppNavigate } from '~/pages';
import dayjs from 'dayjs';
import Sheet from 'react-modal-sheet';
import QrCode from '~/shared/QrCode';
import { Button, Panel } from '~/shared/Theme';
import { DarkSkeleton } from '~/shared/DarkSkeleton';

type ProfileHeaderProps = {
    member?: Member | null;
    isImageLoading?: boolean;
    setIsImageLoading?: (val: boolean) => void;
    profilePictureUri?: string;
};

const ProfileHeader = ({ member, isImageLoading, setIsImageLoading, profilePictureUri }: ProfileHeaderProps) => {

    const {
        mutate: updateMember,
        isLoading: loadingUpload
    } = useUpdateMember();

    const { navigate, params } = useAppNavigate();

    const { profile } = useAppSettings();

    const [isModalOpen, setIsModalOpen] =
        useState(false);

    const [profilePictureAdded, setProfilePictureAdded] =
        useState<boolean>(false);

    const [profilePictureRemoved, setProfilePictureRemoved] =
        useState<boolean>(false);

    const [undoType, setUndoType] =
        useState<string>();

    const {
        radius_sm,
        card_text,
        toast_text,
        toast_success_bg,
        primary,
    } = useTheme();

    useEffect(() => {

        if (profilePictureAdded) {
            setTimeout(() => {

                setProfilePictureAdded(false);
            }, 4000);
        }

        if (profilePictureRemoved) {
            setTimeout(() => {

                setProfilePictureRemoved(false);
            }, 4000);
        }
    }, [profilePictureAdded, profilePictureRemoved]);

    const fileToBase64 = (file) =>

        new Promise((resolve, reject) => {

            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = (e) => reject(e);
        });

    const onImageChange = async event => {

        if (event.target.files && event.target.files[0]) {
            const img = event.target.files[0];
            const base64 = await fileToBase64(img);

            if (member && member.use_custom_profile_picture && member.profile_picture) {
                setUndoType('undo_to_previous');
            }
            else {
                setUndoType('undo_to_default');
            }

            updateMember({
                member: {
                    profile_picture: base64.toString(),
                    use_custom_profile_picture: true
                }
            }, {
                onSuccess: () => {

                    setProfilePictureAdded(true);
                } });
        }
    };

    const formatEmail = (inputEmail: string): string => {

        if (inputEmail.length >= 33) {
            return inputEmail.slice(0, 33) + '...';
        }

        return inputEmail;
    };

    const onRemovePicture = (): void => {

        updateMember({
            member: {
                use_custom_profile_picture: false
            }
        }, { onSuccess: () => {

            setProfilePictureRemoved(true);
        } });
    };

    useEffect(() => {

        if (member && !profilePictureUri) {
            setIsImageLoading(false);
        }
    }, [member]);

    return (
        <>
            <input
                style={{ display: 'none' }}
                type='file'
                id='myImage'
                accept='image/*'
                onChange={onImageChange}
            />
            {/* the input above will interfere with flex gaps if left within the markup below */}
            <VStack
                alignItems={'center'}
                space={4}
            >
                <Panel w={'100%'} alignItems={'center'} space={4} pt={4}>
                    <H1
                        text={'profile.greetings'}
                        textValues={[`${member?.first_name || ''} ${member?.last_name || ''}`]}
                    />
                    <HStack space={4} w={'100%'} alignItems={'center'} justifyContent={'space-evenly'}>
                        { isImageLoading &&
                            <VStack space={2} alignItems={'center'} mx={'auto'} position={'absolute'}>
                                <DarkSkeleton w={'126px'} h={'126px'} borderRadius={'126px'} opacity={isImageLoading ? 1 : 0} />
                                <DarkSkeleton w={'40px'} h={'16px'} borderRadius={'126px'} opacity={isImageLoading ? 1 : 0} />
                            </VStack>
                        }
                        <VStack flex={1} alignItems={'center'} justifyContent={'center'}>
                            <VStack opacity={isImageLoading ? 0 : 1}>
                                <Button
                                    loading={loadingUpload}
                                    variant={'text'}
                                    modifier={'unlocked'}
                                    onPress={() => setIsModalOpen(true)}
                                >
                                    { profilePictureUri ? (
                                        <Box
                                            style={{
                                                width: '126px',
                                                height: '126px',
                                                borderRadius: '126px',
                                                backgroundImage: `url(${profilePictureUri})`,
                                                backgroundSize: 'cover',
                                                backgroundPosition: 'center',
                                                position: 'relative',
                                            }}
                                        >
                                            <img
                                                src={profilePictureUri}
                                                onLoad={() => setIsImageLoading && setIsImageLoading(false)}
                                                alt=""
                                                style={{
                                                    display: 'none',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <SpIcon color={primary} icon={faUserCircle} size={126} />
                                    )}
                                </Button>
                                <Button
                                    text={'profile.edit'}
                                    variant={'text'}
                                    modifier={'small'}
                                    disabled={isModalOpen}
                                    onPress={() => setIsModalOpen(true)}
                                />
                            </VStack>
                        </VStack>
                        { profile?.profileQRCode &&
                            <Divider w={1} h={'120px'} bg={primary} />
                        }
                        { profile?.profileQRCode && member && member.phone_number &&
                            <Box flex={1} alignItems={'center'}>
                                <Box w={150} h={150}>
                                    <QrCode value={member.phone_number} />
                                </Box>
                            </Box>
                        }
                    </HStack>
                </Panel>

                <VStack space={4} w={'100%'} px={4}>
                    <HStack space={4} w={'100%'} justifyContent={'space-evenly'}>
                        <Button
                            flex={1}
                            text={'profileMenu.recent_history'}
                            onPress={() => navigate(`w/${params.wallet_id}/profile/menu/history`)}
                        />
                        <Button
                            flex={1}
                            text={'profile.my_account'}
                            onPress={() => navigate(`w/${params.wallet_id}/profile/menu`)}
                        />
                    </HStack>
                    { member && member.phone_number &&
                        <HStack alignItems={'center'} space={2}>
                            <SpIcon
                                color={primary}
                                icon={faPhone}
                            />
                            <Caption>
                                <Caption fontWeight={'bold'} text={'profile.phone'} />
                                <Caption ml={1}>{ member?.phone_number }</Caption>
                            </Caption>
                        </HStack>
                    }
                    { member && member.email &&
                        <HStack alignItems={'center'} space={2}>
                            <SpIcon
                                color={primary}
                                icon={faEnvelope}
                            />
                            <Caption overflowX={'hidden'}>
                                <Caption fontWeight={'bold'} text={'profile.email'} />
                                <Caption ml={1}>{ formatEmail(member.email) }</Caption>
                            </Caption>
                        </HStack>
                    }
                    { member && member.birthday &&
                        <HStack alignItems={'center'} space={2}>
                            <SpIcon
                                color={primary}
                                icon={faBirthdayCake}
                            />
                            <Caption>
                                <Caption fontWeight={'bold'} text={'profile.birthday'} />
                                <Caption ml={1}>{dayjs(member.birthday).format('MM/DD/YYYY')}</Caption>
                            </Caption>
                        </HStack>
                    }
                    { member?.address1 &&
                        <HStack alignItems={'center'} space={2}>
                            <SpIcon
                                color={primary}
                                icon={faHome}
                            />
                            <Caption>
                                { member.address1 || '' } { member.address2 || '' }
                            </Caption>
                        </HStack>
                    }
                </VStack>

                <Sheet
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    style={{
                        width: 'clamp(320px, 100vw, 480px)',
                        height: '400px',
                        marginTop: 'auto',
                        marginRight: 'auto',
                        marginLeft: 'auto',
                    }}
                >
                    <Sheet.Container>
                        <Sheet.Header />
                        <Sheet.Content>
                            <Box w={'100%'} color={card_text}>
                                <VStack alignItems='center' px={4} space={4}>
                                    <H4 fontWeight={'bold'} text={'profile.upload_profile'} />
                                    <label htmlFor='myImage'>
                                        <Button text={'profile.choose_from_library'} htmlFor='myImage' />
                                    </label>
                                    { profilePictureUri ?
                                        <Button text={'profile.remove_photo'} onPress={onRemovePicture} />
                                        : <></>
                                    }
                                    <Button text={'profile.cancel'} variant={'text'} onPress={() => setIsModalOpen(false)} />
                                    { profilePictureAdded || profilePictureRemoved &&
                                        <HStack
                                            bg={toast_success_bg}
                                            px={6}
                                            py={3}
                                            rounded={radius_sm}
                                            alignItems={'center'}
                                            color={toast_text}
                                        >
                                            <Caption
                                                text={profilePictureAdded ? 'profile.profile_image_added' : 'profile.profile_image_removed'}
                                            />
                                            <Button
                                                text={'profile.undo'}
                                                variant={'text'}
                                                modifier={'small'}
                                                color={toast_text}
                                                underline
                                                onPress={() => {

                                                    updateMember({
                                                        member: {
                                                            use_custom_profile_picture: profilePictureAdded ? undoType : true,
                                                        },
                                                    });
                                                }}
                                            />
                                        </HStack>
                                    }
                                </VStack>
                            </Box>
                        </Sheet.Content>
                    </Sheet.Container>
                    <Sheet.Backdrop />
                </Sheet>
            </VStack>
        </>
    );
};


export const ProfileHeaderSkeleton = () => {

    return (
        <VStack
            alignItems={'center'}
            space={4}
        >
            <Panel w={'100%'} alignItems={'center'} space={4} pt={4}>
                <DarkSkeleton w={'60%'} h={'30px'} />
                <HStack space={4} w={'100%'} alignItems={'center'} justifyContent={'space-evenly'}>
                    <VStack flex={1} alignItems={'center'} justifyContent={'center'}>
                        <VStack>
                            <DarkSkeleton w={'128px'} h={'156px'} />
                        </VStack>
                    </VStack>
                </HStack>
            </Panel>

            <VStack space={4} w={'100%'} px={4} pb={4}>
                <HStack space={4} w={'100%'} justifyContent={'space-evenly'}>
                    <DarkSkeleton flex={1} />
                    <DarkSkeleton flex={1} />
                </HStack>
                <DarkSkeleton h={21} w={'60%'} />
                <DarkSkeleton h={21} w={'50%'} />
                <DarkSkeleton h={21} w={'40%'} />
            </VStack>
        </VStack>
    );
};

export default ProfileHeader;
